import React, { useState, useEffect } from 'react';
import {
  Box, Button, Grid, TextField, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, Alert, Pagination, Tooltip,
} from '@mui/material';
import { Add, Edit, Delete, OilBarrel, Refresh, CalendarMonth, Schedule } from '@mui/icons-material';
import { toast } from 'react-toastify';
import frotaService from '../../services/frotaService';
import { TrocaOleo } from '../../types/frota';
import VeiculoSelect from '../../components/selects/VeiculoSelect';
import EscritorioSelect from '../../components/selects/EscritorioSelect';
import FrotaPageLayout, { FrotaStatItem } from '../../components/frota/FrotaPageLayout';
import SysDateRangePicker from '../../components/SysDateRangePicker';
import { formatarValorOnInput, parsearValorFormatado, formatarMoedaParaInput } from '../../utils/formatToView';

const dateToYmd = (date: Date | null) => {
  if (!date) return '';
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().split('T')[0];
};

const TrocasOleoPage: React.FC = () => {
  const [items, setItems] = useState<TrocaOleo[]>([]);
  const [loading, setLoading] = useState(true);
  const [dataRange, setDataRange] = useState<{ startDate: Date | null; endDate: Date | null }>({ startDate: null, endDate: null });
  const [filtroVeiculoId, setFiltroVeiculoId] = useState<number | null>(null);
  const [proximaDataRange, setProximaDataRange] = useState<{ startDate: Date | null; endDate: Date | null }>({ startDate: null, endDate: null });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [stats, setStats] = useState({ total: 0, esteMes: 0, comProximaTroca: 0 });
  const [openForm, setOpenForm] = useState(false);
  const [selected, setSelected] = useState<TrocaOleo | null>(null);
  const [openDelete, setOpenDelete] = useState(false);
  const [form, setForm] = useState({
    veiculo_id: null as number | null, kilometragem: 0, data_troca: new Date().toISOString().split('T')[0],
    tipo_oleo: '', proxima_troca_km: 0, proxima_troca_data: '', valor: 0, mecanica: '',
    observacoes: '', escritorio_id: null as number | null,
  });
  const [valorFormatado, setValorFormatado] = useState('0,00');

  const loadStats = async () => {
    try {
      const totalRes = await frotaService.listarTrocasOleo({ limit: 1 });
      const listaRes = await frotaService.listarTrocasOleo({ limit: 500 });
      const mesAtual = new Date().toISOString().slice(0, 7);
      const lista = listaRes.data || [];
      setStats({
        total: totalRes.pagination?.total || 0,
        esteMes: lista.filter((t) => t.data_troca?.startsWith(mesAtual)).length,
        comProximaTroca: lista.filter((t) => t.proxima_troca_km || t.proxima_troca_data).length,
      });
    } catch {
      /* mantém stats anteriores */
    }
  };

  const buildFiltros = () => ({
    data_inicio: dateToYmd(dataRange.startDate) || undefined,
    data_fim: dateToYmd(dataRange.endDate) || undefined,
    veiculo_id: filtroVeiculoId ?? undefined,
    proxima_data_inicio: dateToYmd(proximaDataRange.startDate) || undefined,
    proxima_data_fim: dateToYmd(proximaDataRange.endDate) || undefined,
    page,
    limit: 20,
  });

  const load = async () => {
    try {
      setLoading(true);
      const res = await frotaService.listarTrocasOleo(buildFiltros());
      setItems(res.data || []);
      setTotalPages(res.pagination?.pages || 1);
    } catch {
      toast.error('Erro ao carregar trocas de óleo');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadStats(); }, []);
  useEffect(() => { load(); }, [dataRange, filtroVeiculoId, proximaDataRange, page]);

  const resetForm = () => {
    setValorFormatado('0,00');
    setForm({
      veiculo_id: null, kilometragem: 0, data_troca: new Date().toISOString().split('T')[0],
      tipo_oleo: '', proxima_troca_km: 0, proxima_troca_data: '', valor: 0, mecanica: '',
      observacoes: '', escritorio_id: null,
    });
  };

  const openEdit = (t: TrocaOleo) => {
    setSelected(t);
    const valor = Number(t.valor) || 0;
    setValorFormatado(formatarMoedaParaInput(valor));
    setForm({
      veiculo_id: t.veiculo_id, kilometragem: t.kilometragem, data_troca: t.data_troca,
      tipo_oleo: t.tipo_oleo || '', proxima_troca_km: t.proxima_troca_km || 0,
      proxima_troca_data: t.proxima_troca_data || '', valor: Number(t.valor) || 0,
      mecanica: t.mecanica || '', observacoes: t.observacoes || '', escritorio_id: t.escritorio_id,
    });
    setOpenForm(true);
  };

  const handleSave = async () => {
    if (!form.veiculo_id || !form.kilometragem || !form.data_troca) {
      toast.error('Preencha veículo, KM e data');
      return;
    }
    try {
      const payload = {
        veiculo_id: form.veiculo_id,
        kilometragem: form.kilometragem,
        data_troca: form.data_troca,
        tipo_oleo: form.tipo_oleo || undefined,
        proxima_troca_km: form.proxima_troca_km || undefined,
        proxima_troca_data: form.proxima_troca_data || undefined,
        valor: form.valor,
        mecanica: form.mecanica || undefined,
        observacoes: form.observacoes || undefined,
        escritorio_id: form.escritorio_id ?? undefined,
      };
      if (selected) {
        await frotaService.atualizarTrocaOleo(selected.id, payload);
        toast.success('Atualizado');
      } else {
        await frotaService.criarTrocaOleo(payload);
        toast.success('Troca registrada');
      }
      setOpenForm(false);
      setSelected(null);
      load();
      loadStats();
    } catch {
      toast.error('Erro ao salvar');
    }
  };

  const handleDelete = async () => {
    if (!selected) return;
    try {
      await frotaService.excluirTrocaOleo(selected.id);
      toast.success('Excluída');
      setOpenDelete(false);
      load();
      loadStats();
    } catch {
      toast.error('Erro ao excluir');
    }
  };

  const statItems: FrotaStatItem[] = [
    { key: 'total', label: 'Total registradas', value: stats.total, color: '#9C27B0', icon: <OilBarrel /> },
    { key: 'mes', label: 'Este mês', value: stats.esteMes, color: '#2196F3', icon: <CalendarMonth /> },
    { key: 'proxima', label: 'Com próxima troca', value: stats.comProximaTroca, color: '#FF9800', icon: <Schedule /> },
  ];

  return (
    <>
      <FrotaPageLayout
        title="Trocas de Óleo"
        subtitle="Histórico de trocas, agendamentos e controle de manutenção preventiva"
        icon={<OilBarrel fontSize="large" />}
        iconColor="#9C27B0"
        stats={statItems}
        loading={loading}
        actions={
          <>
            <Button variant="outlined" startIcon={<Refresh />} onClick={() => { load(); loadStats(); }} sx={{ borderRadius: 2 }}>
              Atualizar
            </Button>
            <Button startIcon={<Add />} variant="contained" onClick={() => { resetForm(); setSelected(null); setOpenForm(true); }} sx={{ borderRadius: 2, boxShadow: 2 }}>
              Nova troca
            </Button>
          </>
        }
        filters={
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <SysDateRangePicker
                label="Data da troca"
                value={dataRange}
                onChange={(range) => { setDataRange(range); setPage(1); }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <VeiculoSelect
                label="Veículo"
                value={filtroVeiculoId}
                onChange={(v) => { setFiltroVeiculoId(v); setPage(1); }}
                apenasAtivos={false}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <SysDateRangePicker
                label="Próxima data"
                value={proximaDataRange}
                onChange={(range) => { setProximaDataRange(range); setPage(1); }}
              />
            </Grid>
          </Grid>
        }
      >
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell sx={{ fontWeight: 700 }}>Data</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Veículo</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>KM</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Tipo óleo</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Próxima (KM)</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Próxima (data)</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Valor</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((t) => (
                <TableRow key={t.id} hover>
                  <TableCell>{t.data_troca}</TableCell>
                  <TableCell>{t.veiculo?.placa}</TableCell>
                  <TableCell>{t.kilometragem?.toLocaleString('pt-BR')}</TableCell>
                  <TableCell>{t.tipo_oleo || '-'}</TableCell>
                  <TableCell>{t.proxima_troca_km?.toLocaleString('pt-BR') || '-'}</TableCell>
                  <TableCell>{t.proxima_troca_data || '-'}</TableCell>
                  <TableCell>{t.valor ? frotaService.formatCurrency(Number(t.valor)) : '-'}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Editar"><IconButton size="small" onClick={() => openEdit(t)}><Edit /></IconButton></Tooltip>
                    <Tooltip title="Excluir"><IconButton size="small" color="error" onClick={() => { setSelected(t); setOpenDelete(true); }}><Delete /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {items.length === 0 && <TableRow><TableCell colSpan={8}><Alert severity="info">Nenhuma troca registrada</Alert></TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
        {totalPages > 1 && (
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
            <Pagination count={totalPages} page={page} onChange={(_, p) => setPage(p)} />
          </Box>
        )}
      </FrotaPageLayout>

      <Dialog open={openForm} onClose={() => setOpenForm(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{selected ? 'Editar troca de óleo' : 'Nova troca de óleo'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}><VeiculoSelect value={form.veiculo_id} onChange={(v) => setForm({ ...form, veiculo_id: v })} required /></Grid>
            <Grid item xs={6}><TextField size="small" fullWidth type="date" label="Data troca" InputLabelProps={{ shrink: true }} value={form.data_troca} onChange={(e) => setForm({ ...form, data_troca: e.target.value })} /></Grid>
            <Grid item xs={6}><TextField size="small" fullWidth type="number" label="KM" value={form.kilometragem} onChange={(e) => setForm({ ...form, kilometragem: Number(e.target.value) })} /></Grid>
            <Grid item xs={12}><TextField size="small" fullWidth label="Tipo de óleo" value={form.tipo_oleo} onChange={(e) => setForm({ ...form, tipo_oleo: e.target.value })} /></Grid>
            <Grid item xs={6}><TextField size="small" fullWidth type="number" label="Próxima troca (KM)" value={form.proxima_troca_km} onChange={(e) => setForm({ ...form, proxima_troca_km: Number(e.target.value) })} /></Grid>
            <Grid item xs={6}><TextField size="small" fullWidth type="date" label="Próxima troca (data)" InputLabelProps={{ shrink: true }} value={form.proxima_troca_data} onChange={(e) => setForm({ ...form, proxima_troca_data: e.target.value })} /></Grid>
            <Grid item xs={6}>
              <TextField
                size="small"
                fullWidth type="text" label="Valor (R$)"
                value={valorFormatado}
                onChange={(e) => {
                  const fmt = formatarValorOnInput(e.target.value);
                  setValorFormatado(fmt);
                  setForm((prev) => ({ ...prev, valor: parsearValorFormatado(fmt) }));
                }}
              />
            </Grid>
            <Grid item xs={6}><TextField size="small" fullWidth label="Mecânica" value={form.mecanica} onChange={(e) => setForm({ ...form, mecanica: e.target.value })} /></Grid>
            <Grid item xs={12}><EscritorioSelect value={form.escritorio_id} onChange={(v) => setForm({ ...form, escritorio_id: v as number })} /></Grid>
            <Grid item xs={12}><TextField size="small" fullWidth multiline rows={2} label="Observações" value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenForm(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave}>Salvar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDelete} onClose={() => setOpenDelete(false)}>
        <DialogTitle>Excluir troca de óleo?</DialogTitle>
        <DialogActions>
          <Button onClick={() => setOpenDelete(false)}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>Excluir</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TrocasOleoPage;
